import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

const LANGS = [
    { code: "en", label: "English" },
    { code: "es", label: "Español" },
    { code: "la", label: "Latina" },
];

const T = {
    kicker:   { en: "The Way of the Cross", es: "El Vía Crucis", la: "Via Crucis" },
    title:    { en: "Stations of the Cross", es: "Estaciones del Vía Crucis", la: "Stationes Viae Crucis" },
    subtitle: {
        en: "Walk with the sorrowful Mother, meditation by meditation, from Pilate's judgment to the sealed tomb.",
        es: "Camina con la Madre dolorosa, meditación por meditación, desde el juicio de Pilato hasta el sepulcro sellado.",
        la: "Cum dolorosa Matre, meditatione post meditationem, ambula a iudicio Pilati ad sepulcrum obsignatum.",
    },
    station:  { en: "Station", es: "Estación", la: "Statio" },
    scripture:{ en: "Scripture", es: "Escritura", la: "Scriptura" },
    meditation:{ en: "Meditation", es: "Meditación", la: "Meditatio" },
    stabat:   { en: "Stabat Mater", es: "Stabat Mater", la: "Stabat Mater" },
    method:   {
        en: "By the method of Saint Francis of Assisi",
        es: "Según el método de San Francisco de Asís",
        la: "Iuxta methodum Sancti Francisci Assisiensis",
    },
    versicle: {
        en: "V. We adore Thee, O Christ, and we praise Thee.\nR. Because by Thy holy Cross Thou hast redeemed the world.",
        es: "V. Te adoramos, oh Cristo, y te bendecimos.\nR. Porque por tu santa Cruz redimiste al mundo.",
        la: "V. Adoramus te, Christe, et benedicimus tibi.\nR. Quia per sanctam crucem tuam redemisti mundum.",
    },
    footnote: {
        en: "Traditional sacred art from the fifteenth through the nineteenth century accompanies each station.",
        es: "Arte sacro tradicional de los siglos XV al XIX acompaña cada estación.",
        la: "Ars sacra tradita a saeculo decimo quinto ad decimum nonum unamquamque stationem comitatur.",
    },
};

const ROMAN = ["I","II","III","IV","V","VI","VII","VIII","IX","X","XI","XII","XIII","XIV"];

const Stations = () => {
    const [data, setData] = useState(null);
    const [lang, setLang] = useState("en");

    useEffect(() => {
        api.get("/stations").then(r => setData(r.data));
    }, []);

    if (!data) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-24 text-center text-muted-foreground italic" data-testid="stations-loading">
                Loading the way of the Cross…
            </div>
        );
    }

    return (
        <div className="relative" data-testid="stations-page">
            {/* HERO */}
            <section className="relative border-b border-border overflow-hidden">
                <div className="absolute inset-0 -z-0">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/e/ef/El_Greco_-_Christ_Carrying_the_Cross_-_WGA10508.jpg/1200px-El_Greco_-_Christ_Carrying_the_Cross_-_WGA10508.jpg" alt="" className="w-full h-full object-cover opacity-15" />
                    <div className="absolute inset-0 bg-background/80" />
                </div>
                <div className="relative max-w-4xl mx-auto px-6 py-20 text-center">
                    <div className="small-caps text-xs text-muted-foreground mb-3">{T.kicker[lang]}</div>
                    <h1 className="font-serif text-4xl md:text-5xl leading-tight" data-testid="stations-title">{T.title[lang]}</h1>
                    <p className="mt-5 text-muted-foreground italic max-w-2xl mx-auto">{T.subtitle[lang]}</p>
                    <div className="mt-4 small-caps text-[10px] text-muted-foreground">{T.method[lang]}</div>
                </div>
            </section>

            <div className="max-w-4xl mx-auto px-6 md:px-10 py-12">
                {/* LANGUAGE PICKER */}
                <div className="flex items-center justify-center gap-1 mb-10" data-testid="stations-lang">
                    {LANGS.map(l => (
                        <button key={l.code} onClick={() => setLang(l.code)} data-testid={`stations-lang-${l.code}`}
                            className={`px-3 py-1.5 text-xs small-caps border ${lang===l.code ? 'border-liturgy accent-liturgy' : 'border-border text-muted-foreground hover:accent-liturgy'}`}>
                            {l.label}
                        </button>
                    ))}
                </div>

                {/* OPENING PRAYER */}
                <div className="missal-card p-8 md:p-12 mb-12" data-testid="stations-opening">
                    <div className="small-caps text-[10px] accent-liturgy mb-3">{data.opening.title[lang] || data.opening.title.en}</div>
                    <p className="font-body text-base leading-loose text-foreground/90 italic">{data.opening.text[lang] || data.opening.text.en}</p>
                </div>

                {/* THE 14 STATIONS */}
                <div className="space-y-16">
                    {data.stations.map((st, i) => (
                        <section key={st.number} className="scroll-mt-24" id={`station-${st.number}`} data-testid={`station-${st.number}`}>
                            <div className="grid md:grid-cols-5 gap-8 items-start">
                                {/* ART — 2 of 5 cols */}
                                <div className="md:col-span-2">
                                    <div className="aspect-[3/4] overflow-hidden bg-parchment-100 shadow-sm">
                                        <img src={st.art.url} alt={st.art.title}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                            onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                    </div>
                                    <div className="mt-3 text-[10px] text-muted-foreground italic text-center">
                                        <span className="font-serif not-italic">{st.art.title}</span>
                                        {" — "}<span>{st.art.artist}</span>
                                        <span className="ml-1">({st.art.year})</span>
                                    </div>
                                </div>

                                {/* TEXT — 3 of 5 cols */}
                                <div className="md:col-span-3 space-y-5">
                                    <div>
                                        <div className="small-caps text-[10px] accent-liturgy">{T.station[lang]} {ROMAN[i]}</div>
                                        <h2 className="font-serif text-2xl md:text-3xl leading-tight mt-1">{st.title[lang] || st.title.en}</h2>
                                        {lang !== "la" && (
                                            <div className="text-sm italic text-muted-foreground mt-1">{st.title.la}</div>
                                        )}
                                    </div>

                                    {/* Versicle & response */}
                                    <div className="text-sm text-foreground/80 whitespace-pre-line leading-relaxed">
                                        {T.versicle[lang]}
                                    </div>

                                    {/* Meditation */}
                                    <div>
                                        <div className="small-caps text-[10px] text-muted-foreground mb-2">{T.meditation[lang]} · {st.scripture_ref[lang] || st.scripture_ref.en}</div>
                                        <p className="font-body text-base leading-relaxed text-foreground/90">
                                            {st.meditation[lang] || st.meditation.en}
                                        </p>
                                    </div>

                                    {/* Stabat Mater refrain */}
                                    <div className="border-l-2 border-liturgy/40 pl-4 py-1">
                                        <div className="small-caps text-[10px] accent-liturgy mb-2">{T.stabat[lang]}</div>
                                        <p className="font-serif text-sm italic leading-relaxed text-foreground/80 whitespace-pre-line">
                                            {(lang === "la" ? st.stabat.la : st.stabat.en).replace(/ \/ /g, "\n")}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </section>
                    ))}
                </div>

                {/* CLOSING PRAYER */}
                <div className="missal-card p-8 md:p-12 mt-16" data-testid="stations-closing">
                    <div className="small-caps text-[10px] accent-liturgy mb-3">{data.closing.title[lang] || data.closing.title.en}</div>
                    <p className="font-body text-base leading-loose text-foreground/90 italic">{data.closing.text[lang] || data.closing.text.en}</p>
                </div>

                <p className="mt-10 text-center text-xs text-muted-foreground italic max-w-lg mx-auto">
                    {T.footnote[lang]}
                </p>
            </div>
        </div>
    );
};

export default Stations;
